import React, { useState, ReactNode } from 'react';
import { compose } from 'redux';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { AppState } from '@om-fe/core/core/types';
import { connect } from 'react-redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import {
    FavoriteMatchListDefaultProps,
    FavoriteMatchListPublicProps,
    FavoriteMatchListStoreModel,
    FavoriteMatchListStoreProps,
} from './favorite-match-list.constants';
import { areFavoriteMatchListTopicPropsEqual, getFavoriteMatchListSliceName } from './favorite-match-list.utils';
import { getFavoriteMatchList, removeFavoriteMatchList, updateFavoriteMatchList } from './favorite-match-list.actions';
import MatchList from '../../match-list';
import getSlice from '@om-fe/core/utils/get-slice';
import { DisciplineLiveStatus, SessionInfoModel } from '@om-fe/models';
import { BemMethod, createBem } from '@om-fe/utils';
import { GeneralIcon } from '@om-fe/widgets';
import { FavoritesStoreState } from '@om-fe/core/components/favorite-events/favorite-state.constants';
import { favoriteSlice } from '@om-fe/core/components/favorite-button/favorite-state.store';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';

type FavoriteMatchListProps = Partial<FavoriteMatchListDefaultProps>
    & FavoriteMatchListPublicProps
    & FavoriteMatchListStoreProps
    & I18nContextInterface;

const bem: BemMethod = createBem('MatchListMixed');

function FavoriteMatchListComponent({
    instanceId,
    sliceKey,
    gettext,
    hasHeader = false,
    filteredSportIds,
    favoriteMatchIds,
    itemLayout,
    matchId,
    ...props
}: FavoriteMatchListProps){
    const [isExpanded, setIsExpanded] = useState(true);
    const toggleIsExpanded = ():void => {
        setIsExpanded(!isExpanded);
    }
    
    const getFavoriteMatchListNode = (): ReactNode => {
        return (
            <>
                {filteredSportIds?.map((disciplineId: string) => (
                    <MatchList
                        {...props}
                        key={matchId}
                        limitNumberOfEvents={undefined}
                        instanceId={instanceId}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        sliceKey={`${sliceKey}.disciplines[${disciplineId}]`}
                        disciplineId={disciplineId}
                        showMarketsName={true}
                        showMixedMatchListHeader={true}
                        itemLayout={itemLayout}
                        showMatchListHeader={false}
                        showMarketInMatchListGroupHeader={true}
                    />
                ))}
            </>
        );
    }

    const getFavoriteMatchListHeaderNode = (): ReactNode => {

        return (
            <div className={bem('Header')}>
                <button className={bem('CollapseButton')} onClick={toggleIsExpanded}>
                    <span className={bem('Title')}>{gettext('Favorites')}</span>
                    <span className={bem('Counter')} >{favoriteMatchIds!.length}</span>
                    <GeneralIcon name={isExpanded ? 'arr-up' : 'arr-down'} />
                </button>
            </div>
        )
    }

    if (!filteredSportIds || !filteredSportIds.length) {
        return null;
    }

    return (
        <div className={bem()}>
            {hasHeader && getFavoriteMatchListHeaderNode()}
            {(!hasHeader || (hasHeader && isExpanded)) && getFavoriteMatchListNode()}
        </div>
    );
}

const FavoriteMatchList: React.ComponentType<FavoriteMatchListPublicProps> = compose<React.ComponentType<FavoriteMatchListPublicProps>>(
    withTranslation<FavoriteMatchListPublicProps>(),
    preLoader<FavoriteMatchListPublicProps & I18nContextInterface>({
        preload({ props, dispatch }) {
            const { instanceId, matchId, mainMarketsLimit } = props;

            dispatch(getFavoriteMatchList(instanceId, matchId, mainMarketsLimit));
        },
        getReadySlice({ instanceId }: FavoriteMatchListPublicProps): string {
            const slice: string = getFavoriteMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, matchId, mainMarketsLimit } = props;

            if (instanceId !== prevProps.instanceId) {
                dispatch(removeFavoriteMatchList(prevProps.instanceId));
                dispatch(getFavoriteMatchList(instanceId, matchId, mainMarketsLimit));
            } else if (!areFavoriteMatchListTopicPropsEqual(prevProps, props)) {
                dispatch(updateFavoriteMatchList(instanceId, matchId, mainMarketsLimit));
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId } = props;

            dispatch(removeFavoriteMatchList(instanceId));
        },
    }),
    connect<FavoriteMatchListStoreProps, undefined, FavoriteMatchListPublicProps>(
        (state: AppState, props: FavoriteMatchListPublicProps): FavoriteMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getFavoriteMatchListSliceName(instanceId);
            const sessionInfo: SessionInfoModel | undefined = state.session && state.session.info;
            const isAuthenticated: boolean = !!(sessionInfo && sessionInfo.isAuthenticated);

            const slice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(state, sliceKey);
            const favoriteEventsSlice: FavoritesStoreState | undefined = getSlice<FavoritesStoreState>(state, favoriteSlice);
            const favoriteMatchIds: string [] | undefined = favoriteEventsSlice && Object.keys(favoriteEventsSlice.favoriteMatchItems);

            if (!slice) {
                return {
                    sliceKey,
                    ready: false,
                    isAuthenticated,
                    breakpoints: undefined
                };
            }

            const breakpoints: BreakpointsState | undefined = state.breakpoints;

            return {
                sliceKey,
                filteredSportIds: slice.filteredSportIds,
                ready: slice.ready,
                isAuthenticated,
                favoriteMatchIds,
                breakpoints
            };
        },
    ),
)(FavoriteMatchListComponent);
export default FavoriteMatchList;
